import React, { useCallback, useEffect, useRef, useState } from 'react';
import ServerInfoCard from '../components/Shared/ServerInfoCard';
import ProgressStepper from '../components/RconHacking/ProgressStepper';
import ConfigurationPanel from '../components/RconHacking/ConfigurationPanel';
import TerminalOutput from '../components/RconHacking/TerminalOutput';
import RconPortsSection from '../components/RconHacking/RconPortsSection';
import PasswordCrackingPreview from '../components/RconHacking/PasswordCrackingPreview';
import HackingTips from '../components/RconHacking/HackingTips';
import PageTransition from '../components/Shared/PageTransition';
import RconHackingHeader from '../components/RconHacking/RconHackingHeader';
import ProcessStatusDisplay from '../components/Shared/ProcessStatusDisplay';
import RconManualConnect from "../components/RconHacking/RconManualConnect";
import { useAuth } from '../contexts/AuthContext';
import useWebSocket from '../hooks/useWebSocket';
import { getServerInfoAppEndpoint, WS_SERVER_INFO_TOPICS, getServerInfoUserQueueTopic } from '../config/wsServerInfoConfig';
import { RCON_MESSAGE_TYPES, RCON_TOPICS, getRconAppEndpoint, getRconUserQueueTopic } from '../config/wsRconConfig';
import { useServerConnection } from '../contexts/ServerConnectionContext';
import Tips from '../components/Shared/Tips';
import { useNotifications } from '../contexts/NotificationContext';
import { planErrorMath } from '../utils/errorsMatch';
import { handleWebSocketError } from '../utils/websocketErrorHandler';
// Example function to parse progress string (adjust based on backend format)
const parseProgress = (message) => {
    // Attempt to parse JSON first if backend sends structured progress
    try {
        if (typeof message === 'object' && message !== null && message.scanned !== undefined && message.total !== undefined) {
            return {
                scanned: message.scanned,
                total: message.total > 0 ? message.total : 1, // Ensure total is at least 1 if structured
                found: message.found || 0,
            };
        }
    } catch (e) { /* Ignore parsing error, try string matching */ }

    // Fallback to string matching
    const scannedOverTotalMatch = typeof message === 'string' ? message.match(/(\d+)\/(\d+)/) : null; // Looks for "123/1000"
    const foundMatch = typeof message === 'string' ? message.match(/found:\s*(\d+)/i) : null; // Looks for "found: 5"
    const checkedPasswordsMatch = typeof message === 'string' ? message.match(/Checked:\s*(\d+)\s*passwords/i) : null;

    let scanned = 0;
    let total = 1; // Default to 1 to avoid div by zero if no proper total is found
    let found = 0;

    if (scannedOverTotalMatch) {
        scanned = parseInt(scannedOverTotalMatch[1], 10);
        total = parseInt(scannedOverTotalMatch[2], 10);
    } else if (checkedPasswordsMatch) {
        scanned = parseInt(checkedPasswordsMatch[1], 10);
        total = 0; // Indicate that total is unknown or not applicable for percentage bar
    }

    if (foundMatch) {
        found = parseInt(foundMatch[1], 10);
    }

    return {
        scanned: scanned,
        total: total, // Can be 0 if from checkedPasswordsMatch, UI should handle this
        found: found,
    };
};

  const tips = [
    {
      icon: 'fa-bolt',
      title: 'Common RCON Ports',
      description: 'Most servers use port 25575 for RCON by default. Also check ports 25565+10 pattern. Standard RCON port is typically 25575, but some servers use variations like 25576, 25585, or even non-standard ports for security.'
    },
    {
      icon: 'fa-shield-alt',
      title: 'Password Strategy',
      description: 'Start with server name variations, then common Minecraft passwords, then standard wordlists. Effective password guessing order: 1) Server name variations 2) "minecraft", "password", "admin" 3) Top 1000 passwords 4) Full wordlist'
    },
    {
      icon: 'fa-clock',
      title: 'Patience Is Key',
      description: 'RCON cracking can take time. Higher delay values reduce detection risk but make cracking slower. Recommended delay settings: 50ms for fast networks, 150-300ms for balanced approach, 500ms+ for stealthy cracking'
    },
  ];
  
  
  const RconHackingPage = ({openModal}) => {
    const { isAuthenticated, user } = useAuth();
    const { isConnected, sendMessage, subscribe } = useWebSocket();
    const { addNotification } = useNotifications();
  
    const { serverInfo, isConnectedToServer, isConnecting, connectionError } = useServerConnection();
  
    // RCON specific state
    const [currentStep, setCurrentStep] = useState(0);
    const [scanConfig, setScanConfig] = useState({ threads: 25, timeout: 250, scanMode: 'FULL' });
    // Default wordlist - might be overridden when list loads
    const [crackConfig, setCrackConfig] = useState({ wordlist: '', threads: 25, delay: 150 });
    const [foundPorts, setFoundPorts] = useState([]);
    const [terminalLines, setTerminalLines] = useState([]); // For the embedded console
    const [rconStatus, setRconStatus] = useState("Idle");
    const [rconStatusColor, setRconStatusColor] = useState("text-gray-400");
    const [rconError, setRconError] = useState(null);
    const [isRconProcessing, setIsRconProcessing] = useState(false);
    const [selectedPortToCrack, setSelectedPortToCrack] = useState(null);
    const [rconHackedData, setRconHackedData] = useState(null);
    const [progressData, setProgressData] = useState({ scanned: 0, total: 1, found: 0 }); // State for progress bar/stats
    const [availableWordlists, setAvailableWordlists] = useState([]); // State for wordlists
  
    // --- Subscription Refs ---
     const serverInfoSubRef = useRef(null);
     const serverErrorSubRef = useRef(null);
     const rconProgressSubRef = useRef(null);
     const rconErrorSubRef = useRef(null);
     const rconResultSubRef = useRef(null);
  
    // --- Terminal/Console Management ---
    const addTerminalLine = useCallback((text, type = 'info') => {
        const timestamp = new Date().toISOString();
        // For Minecraft specific server responses, we might want a different type
        // or let ProcessStatusDisplay handle § codes if type is 'response'
        setTerminalLines(prev => {
            // Keep a reasonable limit on lines to prevent performance issues
            const limited = prev.length > 300 ? prev.slice(prev.length - 300) : prev;
            return [...limited, { type, text, timestamp }];
        });
    }, []);
  
    const clearTerminal = useCallback(() => {
        setTerminalLines([]);
        setRconError(null);
        setProgressData({ scanned: 0, total: 1, found: 0 }); // Reset progress too
    }, []);
  
     // --- RCON Command Handling ---
     const handleSendCommand = useCallback((command) => {
        if (!rconHackedData || !isConnected || !serverInfo) {
             addTerminalLine("Cannot send command: RCON access not granted or not connected.", "error");
             return;
         }
         // Log the command being sent *before* sending it
         addTerminalLine(`> ${command}`, 'command'); // Indicate it's a sent command
  
         sendMessage(getRconAppEndpoint(RCON_TOPICS.APP_COMMAND), {
             // Send necessary details for the backend to execute the command
             ip: serverInfo.ip,
             port: rconHackedData.port,
             password: rconHackedData.password, // !! Be mindful of security sending password over WS
             command: command
         });
     }, [rconHackedData, isConnected, serverInfo, sendMessage, addTerminalLine]);
  
  
    // --- WebSocket Callbacks (Ensure isRconProcessing is reset) ---
    const handleRconProgress = useCallback((message, wsError) => {
         if (!message || wsError) {
             console.error("Error in RCON progress message:", wsError || message?.error);
             return;
         }
  
         const { type, data } = message;
  
         switch (type) {
            // ... (other cases remain the same) ...
            case RCON_MESSAGE_TYPES.PASSWORD_BASES_LIST: // Handle receiving password bases
                  if (Array.isArray(data?.bases)) {
                      console.log("Received password bases:", data.bases);
                      setAvailableWordlists(data.bases);
                       // Optionally add a log message
                       addTerminalLine(`Available password bases loaded: ${data.bases.join(', ')}`, 'system');
                       // Set default wordlist if none is selected or current is invalid
                       setCrackConfig(prev => {
                           if (!prev.wordlist || !data.bases.includes(prev.wordlist)) {
                               return { ...prev, wordlist: data.bases[0] || '' }; // Default to first or empty
                           }
                           return prev;
                       });
                  } else {
                       console.warn("Received invalid password bases list:", data);
                       addTerminalLine("Failed to load password bases.", 'warning');
                  }
                 break;
            default:
                // console.warn("Unhandled RCON progress/status type:", type, data); // Keep this minimal
        }
         // Handle common status/progress updates outside the switch if they are repetitive
         if (type === RCON_MESSAGE_TYPES.STATUS_UPDATE && data?.status) {
             setRconStatus(data.status);
             addTerminalLine(`[Status] ${data.status}`, 'system');
             const statusLower = data.status.toLowerCase();
             if (statusLower.includes("scanning")) setRconStatusColor("text-accent-blue");
             else if (statusLower.includes("cracking")) setRconStatusColor("text-accent-purple");
             else if (statusLower.includes("finish") || statusLower.includes("granted")) setRconStatusColor("text-accent-green");
             else if (statusLower.includes("error") || statusLower.includes("failed") || statusLower.includes("incorrect")) setRconStatusColor("text-accent-red");
             else if (statusLower.includes("stop")) setRconStatusColor("text-gray-400");
             else setRconStatusColor("text-gray-400"); // Default/Ready/Idle
         }
         if (type === RCON_MESSAGE_TYPES.PROGRESS_UPDATE && data?.message) {
             //if (!data.message.match(/^\d+\/\d+/)) {
                 addTerminalLine(data.message, 'info');
             //}
             setProgressData(prev => ({ ...prev, ...parseProgress(data.message) }));
         }
          if (type === RCON_MESSAGE_TYPES.PORT_FOUND && data?.port != null) {
              const newPort = { port: data.port };
              setFoundPorts(prev => {
                  if (!prev.some(p => p.port === newPort.port)) {
                      const updatedPorts = [...prev, newPort].sort((a, b) => a.port - b.port);
                      setProgressData(p => ({ ...p, found: updatedPorts.length }));
                      return updatedPorts;
                  }
                  return prev;
              });
              addTerminalLine(`Potential RCON port found: ${newPort.port}`, 'success');
          }
          if (type === RCON_MESSAGE_TYPES.PORT_SCAN_STARTED) {
              setIsRconProcessing(true); setCurrentStep(1); setFoundPorts([]); setRconError(null);
              setProgressData({ scanned: 0, total: 1, found: 0 }); addTerminalLine("Port scan initiated...", 'system');
              setRconStatus("Scanning Ports"); setRconStatusColor("text-accent-blue");
          }
          if (type === RCON_MESSAGE_TYPES.PASSWORD_CRACK_STARTED) {
                setIsRconProcessing(true); // Ensure this is set
              setCurrentStep(2);
              setRconError(null);
              setProgressData({ scanned: 0, total: 1, found: 0 }); // Reset progress for password stage

              // Use port from data if available, otherwise from selectedPortToCrack
              const crackPort = data?.port || selectedPortToCrack;
              if (crackPort) { // Ensure we have a port to display
                addTerminalLine(`Password cracking initiated on port ${crackPort}`, 'system');
                setRconStatus(`Cracking Port ${crackPort}`);
                setRconStatusColor("text-accent-purple");
                if (!selectedPortToCrack && data?.port) { // If selectedPortToCrack wasn't set but backend provides it
                    setSelectedPortToCrack(data.port);
                }
              } else {
                addTerminalLine(`Password cracking initiated (port not specified in message, current: ${selectedPortToCrack || 'unknown'})`, 'system');
                setRconStatus(`Cracking Port...`);
                setRconStatusColor("text-accent-purple");
              }
          }
          if (type === RCON_MESSAGE_TYPES.RCON_COMMAND_RESPONSE && data?.response != null) {
              const responseLines = data.response.split('\n');
              responseLines.forEach(line => {
                  // The ProcessStatusDisplay will handle § color codes if the type is 'response'
                  // and if parseMinecraftTextToHtml is implemented properly.
                  addTerminalLine(line.trim() ? line : "[Empty Response Line]", 'response');
              });
          } else if (type === RCON_MESSAGE_TYPES.RCON_COMMAND_RESPONSE) {
               addTerminalLine("[No response or empty response received]", 'response');
          }
  
    }, [addTerminalLine, selectedPortToCrack]); // Removed dependency on crackConfig to avoid loops
  
   // ... (handleRconResult, handleRconError remain the same) ...
   const handleRconResult = useCallback((message, wsError) => {
        // If there's a WebSocket-level error or a message containing an error structure, handle it first.
        if (wsError || (message && message.error)) {
            const errorMsg = handleWebSocketError(
                message, // Pass the message object itself
                wsError,
                openModal,
                addNotification,
                {
                    defaultTitle: 'RCON Error',
                    moduleName: 'RCON Hacking'
                }
            );
            setRconError(errorMsg);
            setRconStatus("Error"); setRconStatusColor("text-accent-red"); setCurrentStep(0);
            setIsRconProcessing(false); // Stop processing on any error received here
            return;
        }

        // If message is null but no wsError (should be rare, but defensive)
        if (!message) {
            console.error("Received null message in handleRconResult without a wsError.");
            setRconError("Received an empty result message from server.");
             setRconStatus("Error"); setRconStatusColor("text-accent-red"); setCurrentStep(0);
             setIsRconProcessing(false); // Ensure stopped
             return;
         }
  
         const { type, data } = message;
  
         if (wsError || message.error) {
            const errorMsg = handleWebSocketError(
                message,
                wsError,
                openModal,
                addNotification,
                {
                    defaultTitle: 'RCON Error',
                    moduleName: 'RCON Hacking' // Имя модуля для логов
                }
            );
             setRconError(errorMsg);
             setRconStatus("Error"); setRconStatusColor("text-accent-red"); setCurrentStep(0);
             setIsRconProcessing(false); // Ensure stopped
             return;
         }
  
        // Process valid results
        switch (type) {
            case RCON_MESSAGE_TYPES.PORT_SCAN_COMPLETED:
                  addTerminalLine("Port scan completed.", 'system');
                  setProgressData(prev => ({
                      ...prev,
                      scanned: data?.scannedCount !== undefined ? data.scannedCount : prev.scanned,
                      total: data?.totalConfigured !== undefined ? data.totalConfigured : (data?.scannedCount !== undefined ? data.scannedCount : prev.total),
                  }));
                  if (data?.portsFound) {
                       // Ports found, backend will proceed to crack. Keep processing.
                       setRconStatus("Port Scan Finished. Awaiting password crack initiation...");
                       setRconStatusColor("text-gray-400"); // Neutral, awaiting next step
                       setCurrentStep(1); // Stay at/go to port selection/config step
                       //setIsRconProcessing(true); // Ensure it remains true or is set if somehow false
                                                // No: if PORT_SCAN_STARTED set it to true, it should remain true.
                                                // Avoid toggling unless necessary.
                  } else {
                       // No ports found, process truly ends here.
                       addTerminalLine("No potential RCON ports found.", 'warning');
                       setRconStatus("Scan Finished (No Ports)");
                       setRconStatusColor("text-accent-orange");
                       setCurrentStep(0);
                       setIsRconProcessing(false);
                  }
                 break; // Keep processing false (already set)
  
            case RCON_MESSAGE_TYPES.PASSWORD_FOUND:
                 if (data?.port && data?.password) {
                     addTerminalLine(`Success! RCON access granted on port ${data.port}.`, 'success');
                     // Don't log password directly to console: addTerminalLine(`Password: ${data.password}`, 'success');
                     setRconHackedData(data);
                     setCurrentStep(3);
                    setRconStatus("RCON Access Granted");
                      setRconStatusColor("text-accent-green");
                      setIsRconProcessing(false); // Explicitly stop processing on 
                 } else {
                     setRconError("Received success message but data incomplete.");
                     setRconStatus("Error"); setRconStatusColor("text-accent-red"); setCurrentStep(0);
                 }
                 break; // Keep processing false
  
            case RCON_MESSAGE_TYPES.PASSWORD_CRACK_FAILED:
                 addTerminalLine(`Password cracking finished on port ${data?.port || selectedPortToCrack || '...'}. Password not found.`, 'warning');
                 setRconStatus("Crack Finished (Failed)");
                 setRconStatus("Error"); setRconStatusColor("text-accent-red"); setCurrentStep(0);
                  setIsRconProcessing(false); // Process stops on success here
                 break; // Keep processing false
  
            case RCON_MESSAGE_TYPES.BRUTE_STOPPED: // Explicit stop message
                 const stopMsg = data?.message || "RCON process stopped.";
                 addTerminalLine(`[Stopped] ${stopMsg}`, 'system');
                 setRconStatus("Stopped");
                 setRconStatusColor("text-gray-400");
setCurrentStep(1); // Go back to port selection
                  setSelectedPortToCrack(null); // Clear selection
                  setIsRconProcessing(false); // Process stops on failure here
                 setProgressData({ scanned: 0, total: 1, found: 0 });
                 break; // Keep processing false
  
            case RCON_MESSAGE_TYPES.SERVER_HACKED: // Usually redundant if PASSWORD_FOUND is handled
                 addTerminalLine("RCON Access Granted!", 'success');
                 setCurrentStep(3);
                 setRconStatus("RCON Access Granted");
                  setRconStatusColor("text-accent-green");
                  // setIsRconProcessing(false); // Already handled by specific success/failure messages
                  setRconHackedData(data); // BUG FIX: Use data, not null. Data should contain {port, password}
                  // setProgressData({ scanned: 0, total: 1, found: 0 }); // Don't reset progress if command console is shown
                  setIsRconProcessing(false); // Ensure processing stops on success here
                  break;

            default:
                console.warn("Unhandled RCON result type:", type, data);
                setIsRconProcessing(false); // Stop on unknown results
        }
    }, [addTerminalLine, selectedPortToCrack, currentStep, openModal, addNotification]); // Added currentStep dependency
  
  
    const handleRconError = useCallback((errorData, wsError) => {
        setIsRconProcessing(false); // Stop processing on error
        // --- CHECK FOR PLAN ERROR ---
        const errorMessage = handleWebSocketError(
            errorData,
            wsError,
            openModal,
            addNotification,
            {
                defaultTitle: 'RCON Error',
                moduleName: 'RCON Hacking' // Имя модуля для логов
            }
        );
        setRconError(errorMessage);
        setRconStatus("Error");
        setRconStatusColor("text-accent-red");
        setCurrentStep(0); // Reset step on error
    }, [openModal, addNotification]); // Corrected dependencies

  
    // --- Subscription Management ---
    useEffect(() => {
         if (!isConnected || !isAuthenticated) {
             // Cleanup function for when component unmounts or disconnects
             return () => {
                  serverInfoSubRef.current?.();
                  serverErrorSubRef.current?.();
                  rconProgressSubRef.current?.();
                  rconResultSubRef.current?.();
                  rconErrorSubRef.current?.();
                  console.log("Cleaned up RCON WS subs due to disconnect/unmount.");
             };
         }
  
         console.log("Setting up WS subs for RCON...");
         // Subscribe and store unsubscribe functions
         rconProgressSubRef.current = subscribe(getRconUserQueueTopic(RCON_TOPICS.USER_PROGRESS), handleRconProgress); // Handles password bases list too
         rconResultSubRef.current = subscribe(getRconUserQueueTopic(RCON_TOPICS.USER_RESULT), handleRconResult);
         rconErrorSubRef.current = subscribe(getRconUserQueueTopic(RCON_TOPICS.USER_ERROR), handleRconError);
  
         // Request password bases if already connected (e.g., on page load/refresh)
         // Check serverInfo *existence* and online status before requesting
         if (serverInfo && serverInfo.online) {
             sendMessage(getRconAppEndpoint(RCON_TOPICS.APP_GET_BASES), {});
         }
  
  
         // Cleanup function for when dependencies change or component unmounts
         return () => {
             console.log("Cleaning up RCON WS subs...");
             serverInfoSubRef.current?.();
             serverErrorSubRef.current?.();
             rconProgressSubRef.current?.();
             rconResultSubRef.current?.();
             rconErrorSubRef.current?.();
         };
         // Dependencies: Re-run if connection status, auth status, or handlers change
    }, [isConnected, isAuthenticated, subscribe, sendMessage, serverInfo, connectionError, handleRconProgress, handleRconResult, handleRconError]);
  
    // --- State Reset Logic ---
    const resetRconState = (keepServerInfoAndBaseLogs = false) => {
        console.log("Resetting RCON State, Keep Base Info:", keepServerInfoAndBaseLogs);
        if (!keepServerInfoAndBaseLogs) {
            setTerminalLines([]); // Clear all logs
            setAvailableWordlists([]); // Clear wordlists on full reset
        } else if (terminalLines.length > 2) { // Keep first 2 logs (connect/version) if resetting RCON part
            setTerminalLines(prev => prev.slice(0, 2));
            addTerminalLine("--- RCON State Reset ---", "system");
        }
        setCurrentStep(0);
        setFoundPorts([]);
        setRconStatus(keepServerInfoAndBaseLogs && serverInfo && serverInfo.online ? "Ready" : "Idle");
        setRconStatusColor("text-gray-400");
        setRconError(null);
        setIsRconProcessing(false); // !! CRUCIAL: Ensure processing is false
        setSelectedPortToCrack(null);
        setRconHackedData(null);
        setProgressData({ scanned: 0, total: 1, found: 0 });
          // Reset crack config wordlist if needed (e.g., to first available or empty)
        setCrackConfig(prev => ({ ...prev, wordlist: availableWordlists[0] || '' }));
    };
  
    const handleManualConnect = useCallback((port, password) => {
        if (!serverInfo || !serverInfo.online || !isConnected || isRconProcessing) {
            addTerminalLine("Cannot connect manually: Check server connection or ongoing process.", "warning");
            return;
        }
        console.log(`Attempting manual RCON connect to ${serverInfo.ip}:${port}`);
        clearTerminal(); // Clear previous logs for a fresh attempt
        setRconError(null);
        setRconHackedData(null);
        setIsRconProcessing(true);
        setRconStatus(`Connecting to RCON ${port}...`);
        setRconStatusColor("text-accent-blue");
        setCurrentStep(2); // Visually indicate connection attempt step
   
        console.log("Port: ", serverInfo.port)
        // Send the new message type
        sendMessage(getRconAppEndpoint(RCON_TOPICS.APP_MANUAL_CONNECT), {
            ip: serverInfo.ip,
            port: port,
            serverPort: serverInfo.port,
            password: password,
        });
    }, [serverInfo, isConnected, isRconProcessing, sendMessage, addTerminalLine, clearTerminal]);
  
    // --- Action Handlers ---
const handleStartRconProcess = useCallback(() => {
         if (!serverInfo || !serverInfo.online) {
            const msg = "Server not connected or is offline.";
            setRconError(msg); // Keep for internal state if needed
            addTerminalLine(`Cannot start: ${msg}`, "warning");
            addNotification({ title: "RCON Process Error", message: msg, icon: 'fa-server', color: 'accent-orange' });
            return;
         }
         if (!isConnected) {
            const msg = "WebSocket not connected. Please wait or refresh.";
            setRconError(msg);
            addTerminalLine(`Cannot start: ${msg}`, "warning");
            addNotification({ title: "Connection Error", message: msg, icon: 'fa-wifi', color: 'accent-red' });
            return;
         }
         if (isRconProcessing) {
            addTerminalLine("Cannot start: Process already running.", "warning"); // Keep as terminal line, not necessarily a popup error
            return;
         }

         // Validate wordlist selection if cracking
          if (selectedPortToCrack && (!crackConfig.wordlist || crackConfig.wordlist === '')) {
               const msg = "Please select a password database before cracking.";
               setRconError(msg);
               addTerminalLine(`Cannot start: ${msg}`, "warning");
               addNotification({ title: "Configuration Incomplete", message: msg, icon: 'fa-list-alt', color: 'accent-yellow' });
               return;
          }

         setRconError(null);
         setRconHackedData(null);
         setProgressData({ scanned: 0, total: 1, found: 0 });
         addTerminalLine(`Initiating RCON process for ${serverInfo.ip}...`, 'system');
         setIsRconProcessing(true); // Set processing state
         setRconStatus("Initiating...");
         setRconStatusColor("text-gray-500");

         const settingsPayload = {
            scanMode: scanConfig.scanMode || 'FULL',
            timeOut: scanConfig.timeout || 250,
            // Ensure a valid password base is selected or defaulted if starting a full process
            // that will eventually crack passwords.
            passwordBase: crackConfig.wordlist, // Will be refined below
            speedMode: (scanConfig.threads > 1 || crackConfig.threads > 1),
            threadsNumb: selectedPortToCrack ? (crackConfig.threads || 25) : (scanConfig.threads || 25),
            selectRconPort: !!selectedPortToCrack,
            rconPort: selectedPortToCrack || 0,
            selectRconData: crackConfig.wordlist === 'manual' && !!crackConfig.manualPassword,
            password: crackConfig.wordlist === 'manual' ? crackConfig.manualPassword || "" : "", // Only send manual password if mode is manual
            secure: false
          };

          // Refine passwordBase for payload
          if (!settingsPayload.selectRconData && settingsPayload.passwordBase !== 'manual') {
            if (!settingsPayload.passwordBase || settingsPayload.passwordBase === '') {
                if (availableWordlists.length > 0) {
                    settingsPayload.passwordBase = availableWordlists[0];
                    addTerminalLine(`Using default password base: ${availableWordlists[0]} as none was explicitly selected.`, 'system');
                    // Optionally update state, but direct payload modification is safer here
                    // setCrackConfig(prev => ({ ...prev, wordlist: availableWordlists[0] }));
                } else {
                    addNotification({ title: "Configuration Error", message: "No password bases loaded and none selected. Cannot start RCON process.", icon: 'fa-list-alt', color: 'accent-red' });
                    setIsRconProcessing(false); // Reset processing state
                    setRconStatus("Error"); setRconStatusColor("text-accent-red");
                    return; // Prevent sending message
                }
            }
          }


         const logicalStartDest = getRconAppEndpoint(RCON_TOPICS.APP_START);
         sendMessage(logicalStartDest, {
             // Backend expects ServerData structure
             server: { ip: serverInfo.ip, port: serverInfo.port || 25565 }, // Use actual server port if available
             settings: settingsPayload
         });

    }, [serverInfo, isConnected, isRconProcessing, scanConfig, crackConfig, selectedPortToCrack, sendMessage, addTerminalLine, addNotification, setRconError, availableWordlists]); // Added availableWordlists
  
     const handleStopRconProcess = useCallback(() => {
         if (!isRconProcessing || !isConnected || !serverInfo) {
             addTerminalLine("No active process to stop or not connected.", "warning");
             return;
         }
         addTerminalLine("Requesting process stop...", "system");
         sendMessage(getRconAppEndpoint(RCON_TOPICS.APP_STOP), { ip: serverInfo.ip }); // Send stop request
         setRconStatus("Stopping...");
         setRconStatusColor("text-gray-400");
         // isRconProcessing state resets via handleRconResult(BRUTE_STOPPED)
     }, [isRconProcessing, isConnected, serverInfo, sendMessage, addTerminalLine]);
  
    // Config/Port Handlers
    const handleScanConfigChange = useCallback((newConfig) => setScanConfig(prev => ({ ...prev, ...newConfig })), []);
    const handleCrackConfigChange = useCallback((newConfig) => setCrackConfig(prev => ({ ...prev, ...newConfig })), []);
    const handleSelectPort = useCallback((portData) => {
        if (isRconProcessing || currentStep === 3) return;
        const port = portData?.port || portData; // Handle receiving just the number or an object
        if (typeof port !== 'number' || port <= 0) {
            console.warn("Invalid port selected:", portData);
            return;
        }
        setSelectedPortToCrack(port);
        addTerminalLine(`Selected port ${port}. Configure cracker settings and click 'Crack Port'.`, 'system');
        setCurrentStep(1); // Stay in config phase until crack starts
        setRconStatus("Port Selected");
        setRconStatusColor("text-gray-400");
    }, [addTerminalLine, isRconProcessing, currentStep]);
  
  
    // --- UI Rendering Logic ---
    const isRconReady = serverInfo && serverInfo.online && !isConnecting && !connectionError;
    // Allow starting if ready, not processing, and not already hacked.
    // Also check if cracking, that a wordlist is selected
    const canStartProcess = isRconReady && !isRconProcessing && currentStep < 3 && (!selectedPortToCrack || (selectedPortToCrack && crackConfig.wordlist));
    const isConsoleInputEnabled = currentStep === 3 && !isRconProcessing && !!rconHackedData; // Enable only when hacked and idle
  
  
    return (
        <PageTransition>
            <div className="p-6 lg:p-8">
                <RconHackingHeader />
  
                <ServerInfoCard />
  
                 {/* Display RCON specific errors (Only if not connecting/no connect error) */}
                 {rconError && !connectionError && !isConnecting && (
                      <div className="mb-4 p-3 bg-red-900/30 border border-accent-red/50 rounded-lg text-accent-red text-sm animate-fade-in">
                         <i className="fas fa-exclamation-triangle mr-2"></i>
                         <strong>RCON Error:</strong> {rconError}
                     </div>
                 )}
                {/* Add the manual connect section here */}
                {!rconHackedData && currentStep < 3 && ( // Show only if connected and not already hacked
                    <RconManualConnect
                        onManualConnect={handleManualConnect}
                        disabled={isRconProcessing || !isRconReady} // Disable if processing or not connected
                    />
                )}
                {/* Main Content Area - REMOVED blur-sm */}
                <div className={`transition-opacity duration-500 ${!serverInfo ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                    {/* Show stepper only if server is connected */}
                    {serverInfo && <ProgressStepper currentStep={currentStep} />}
  
{/* Grid Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                        <ConfigurationPanel
                            className="lg:col-span-1" // Explicitly set span for clarity
                            scanConfig={scanConfig}
                            crackConfig={crackConfig}
                            onScanConfigChange={handleScanConfigChange}
                            onCrackConfigChange={handleCrackConfigChange}
                            onStartProcess={handleStartRconProcess}
                            isProcessing={isRconProcessing}
                            canProcess={canStartProcess} // Use updated logic
                            isHacked={currentStep === 3}
                            selectedPort={selectedPortToCrack}
                            availableWordlists={availableWordlists}
                            onSelectPort={handleSelectPort}
                        />

                        {/* Process Status Display - now spans 2 columns on large screens */}
                        <div className="lg:col-span-2"> {/* Wrapper to control span */}
                            <ProcessStatusDisplay
                                lines={terminalLines}
                                status={rconStatus}
                                statusColor={rconStatusColor}
                                isLoading={isRconProcessing}
                                error={null} // Error is displayed above the grid now
                                progressData={progressData}
                                actions={[
                                    { label: "Clear Log", onClick: clearTerminal, icon: "fa-trash", disabled: isRconProcessing },
                                    {
                                        label: isRconProcessing ? "Stop Process" : "Stop Process", // Keep label consistent
                                        onClick: handleStopRconProcess,
                                        disabled: !isRconProcessing, // Enable ONLY when processing
                                        primary: true, // Or style based on state, e.g., red when processing
                                        icon: "fa-stop-circle",
                                        loadingLabel: "Stopping..." 
                                    }
                                ]}
                                isConsoleInputEnabled={isConsoleInputEnabled}
                                onSendCommand={handleSendCommand}
                                consoleTitle="RCON Process Log"
                                consoleMaxHeight="h-[400px]"
                                // If ProcessStatusDisplay accepts a maxHeight for its internal console:
                                // consoleMaxHeight="h-[350px]" // Example height
                            />
                        </div>
                    </div>

                    {/* Conditional Sections Below Grid */}
                    {serverInfo && !isRconProcessing && ( // Only show these sections when ready and not actively processing
                        <>
                            {/* Found Ports Section - Adapted to pass only necessary props */}
                             {currentStep >= 1 && foundPorts.length > 0 && currentStep < 3 && (
                                 <div className="mb-8">
                                     <h3 className="font-bold text-lg mb-5 flex items-center">
                                         <i className="fas fa-radar mr-2 text-accent-blue"></i>
                                         Detected RCON Ports
                                     </h3>
                                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                         {foundPorts.map((portInfo, index) => (
                                             <div
                                                 key={index}
                                                 onClick={() => handleSelectPort(portInfo)} // Select on click
                                                 className={`bg-dark-600 rounded-2xl p-4 hover:shadow-lg transition-all transform-gpu hover:-translate-y-1 border ${
                                                  selectedPortToCrack === portInfo.port
                                                      ? 'border-accent-purple shadow-glow-purple scale-105'
                                                      : 'border-dark-400 hover:border-accent-blue/50'
                                                  } cursor-pointer group`}
                                             >
                                                  <div className="flex items-center justify-between">
                                                       <div className="flex items-center">
                                                          <i className={`fas fa-network-wired mr-2 ${selectedPortToCrack === portInfo.port ? 'text-accent-purple' : 'text-accent-blue group-hover:text-white'} transition-colors`}></i>
                                                          <span className={`font-medium ${selectedPortToCrack === portInfo.port ? 'text-white' : 'text-gray-300 group-hover:text-white'} transition-colors`}>Port {portInfo.port}</span>
                                                      </div>
                                                      {selectedPortToCrack === portInfo.port && (
                                                          <span className="text-xs bg-accent-purple/20 text-accent-purple px-2 py-0.5 rounded-full">Selected</span>
                                                      )}
                                                       <i className={`fas fa-chevron-right text-xs text-gray-500 group-hover:text-accent-blue transition-all group-hover:translate-x-1 ${selectedPortToCrack === portInfo.port ? 'text-accent-purple' : ''}`}></i>
                                                  </div>
                                                 {/* Add more port details here if provided by backend */}
                                                 {/* <p className="text-xs text-gray-400 mt-1">Response: {portInfo.responseTime || 'N/A'}</p> */}
                                             </div>
                                         ))}
                                     </div>
                                      {!selectedPortToCrack && <p className="text-center text-sm text-gray-400 mt-4">Click a port above to select it for password cracking.</p>}
                                 </div>
                             )}
  
                            {/* No Ports Found Message */}
                            {currentStep === 1 && foundPorts.length === 0 && (
                                <div className="bg-dark-600 rounded-2xl p-6 mb-8 text-center text-gray-400">
                                    <i className="fas fa-search-minus text-3xl mb-3 text-accent-orange"></i>
                                    <p>Port scan completed. No potential RCON ports found.</p>
                                </div>
                            )}
  
                             {/* Port Found, Select Cracker Message */}
                            {currentStep === 1 && foundPorts.length > 0 && !selectedPortToCrack && (
                                <PasswordCrackingPreview />
                            )}
  
                            {/* Success Display */}
                            {currentStep === 3 && rconHackedData && (
                                <div className="bg-dark-600 rounded-2xl p-6 mb-8 border border-accent-green shadow-lg shadow-accent-green/20 transform-gpu hover:scale-[1.01] transition-medium">
                                   <div className="flex justify-between items-center mb-3">
                                      <h3 className="font-bold text-lg text-accent-green flex items-center">
                                         <i className="fas fa-check-circle mr-2 animate-pulse"></i>RCON Access Granted!
                                      </h3>
                                      <span className="text-xs bg-accent-green/20 text-accent-green px-2 py-1 rounded-full">Success</span>
                                   </div>
                                    <p className="font-mono text-sm bg-dark-700 p-3 rounded-lg border border-dark-400">
                                        <span className="text-gray-400 block mb-1">IP: {serverInfo?.ip || 'N/A'}</span>
                                        <span className="text-gray-400 block mb-1">Port: <span className="text-white">{rconHackedData.port}</span></span>
                                        <span className="text-gray-400">Password: <span className="text-white break-all">{rconHackedData.password}</span></span>
                                    </p>
                                     <p className="text-xs text-gray-400 mt-3">You can now send commands via the console above.</p>
                                </div>
                            )}
                         </>
                    )}
                </div>
  
                <Tips
                    title="RconHackingTips"
                    icon='fa-lightbulb'
                    tips={tips}
                    accentColor='yellow'
                />
                {/* <HackingTips /> */}
            </div>
        </PageTransition>
    );
};

export default RconHackingPage;